import { useState, useMemo } from 'react';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import DirectoryHero from './components/DirectoryHero';
import MobileCategoryBar from './components/MobileCategoryBar';
import DirectoryFilters from './components/DirectoryFilters';
import DirectoryGrid from './components/DirectoryGrid';
import DirectoryCTA from './components/DirectoryCTA';
import CategorySEOContent from './components/CategorySEOContent';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';
import AdDisplay from '../../components/AdDisplay';
import { directoryBusinesses } from '../../mocks/directory';

export default function DirectoryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  // Generate category-specific schema
  const generateCategorySchema = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Roseville Business Directory',
      description: 'Comprehensive directory of local businesses in Roseville, California',
      url: 'https://roseville.org/directory'
    };

    // Add ItemList schema when category is filtered
    if (activeCategory !== 'all') {
      const categoryBusinesses = filtered.slice(0, 10); // Top 10 businesses
      return [
        baseSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: categoryBusinesses.map((business, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'LocalBusiness',
              name: business.name,
              description: business.tagline,
              address: {
                '@type': 'PostalAddress',
                streetAddress: business.address.split(',')[0],
                addressLocality: 'Roseville',
                addressRegion: 'CA',
                postalCode: business.address.match(/\d{5}/)?.[0] || '95678'
              },
              telephone: business.phone,
              url: business.website,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: business.rating,
                reviewCount: business.reviews
              }
            }
          }))
        }
      ];
    }

    return baseSchema;
  };

  useSEO({
    title: 'Roseville CA Business Directory - Dentists, Plumbers, Restaurants, Hair Salons & Auto Repair',
    description: 'Find the best local businesses in Roseville California. Search dentists in Roseville CA, plumbers near me, hair salons, auto repair shops, restaurants, and more. Verified reviews and contact info.',
    keywords: 'Roseville CA businesses, dentists in Roseville CA, plumbers Roseville California, hair salons Roseville, auto repair Roseville CA, restaurants Roseville, local business directory Roseville',
    canonical: '/directory',
    ogImage: 'https://public.readdy.ai/ai/img_res/91abac91b347a28dc2398f32e06f79ce.jpg',
    schema: generateCategorySchema()
  });

  const filtered = useMemo(() => {
    let results = [...directoryBusinesses];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.tagline.toLowerCase().includes(q) ||
          b.description.toLowerCase().includes(q) ||
          b.category.toLowerCase().includes(q) ||
          b.address.toLowerCase().includes(q)
      );
    }

    // Category
    if (activeCategory !== 'all') {
      results = results.filter((b) => b.category === activeCategory);
    }

    // Rating
    if (minRating > 0) {
      results = results.filter((b) => b.rating >= minRating);
    }

    // Verified
    if (verifiedOnly) {
      results = results.filter((b) => b.verified);
    }

    // Sort
    switch (sortBy) {
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'reviews':
        results.sort((a, b) => b.reviews - a.reviews);
        break;
      case 'name':
        results.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'newest':
        results.sort((a, b) => b.yearEstablished - a.yearEstablished);
        break;
      case 'featured':
      default:
        results.sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return b.rating - a.rating;
        });
        break;
    }

    return results;
  }, [searchQuery, activeCategory, minRating, verifiedOnly, sortBy]);

  const handleClearAll = () => {
    setActiveCategory('all');
    setMinRating(0);
    setVerifiedOnly(false);
    setSortBy('featured');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar />
      <DirectoryHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalResults={filtered.length}
      />
      
      {/* Directory Top Ad */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AdDisplay placementId="directory-top" />
      </div>
      
      {/* Business Owner CTA Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 border-y border-teal-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-store-2-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Own a Business in Roseville?</h3>
                <p className="text-sm text-teal-100">Get discovered by thousands of local customers. List your business today!</p>
              </div>
            </div>
            <div className="flex gap-3">
              <a
                href="/submit-business"
                className="px-6 py-3 bg-white text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-line mr-2"></i>
                List Your Business
              </a>
              <a
                href="/owner-dashboard"
                className="px-6 py-3 bg-teal-800 text-white rounded-lg hover:bg-teal-900 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-dashboard-line mr-2"></i>
                Manage Listing
              </a>
            </div>
          </div>
        </div>
      </div>

      <MobileCategoryBar
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-8">
          <div className="hidden lg:block">
            <DirectoryFilters
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              minRating={minRating}
              onMinRatingChange={setMinRating}
              verifiedOnly={verifiedOnly}
              onVerifiedChange={setVerifiedOnly}
              sortBy={sortBy}
              onSortChange={setSortBy}
              onClearAll={handleClearAll}
            />
          </div>
          <div className="flex-1">
            {/* Category SEO Content */}
            <CategorySEOContent category={activeCategory} />
            
            <DirectoryGrid businesses={filtered} />
          </div>
        </div>
      </section>

      {/* Data Sources Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-shield-check-line text-teal-600 text-lg"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Business Directory Data Sources</h3>
              <p className="text-xs text-gray-600 leading-relaxed mb-3">
                Our business directory is built on verified information to help you make informed decisions about local services in Roseville.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Business Information</p>
                  <ul className="text-[10px] text-gray-500 space-y-0.5 leading-relaxed">
                    <li>• Verified by business owners through our platform</li>
                    <li>• Contact details and hours updated regularly</li>
                    <li>• Establishment dates from business registration records</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Ratings & Reviews</p>
                  <ul className="text-[10px] text-gray-500 space-y-0.5 leading-relaxed">
                    <li>• Aggregated from verified customer reviews</li>
                    <li>• Moderated for authenticity and quality</li>
                    <li>• Updated in real-time as new reviews are submitted</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                <p className="text-[11px] text-gray-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>Data last updated: March 10, 2025
                </p>
                <a href="/data-sources" className="inline-flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer">
                  View All Data Sources <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DirectoryCTA />
      <MailchimpNewsletter />
      <Footer />
    </div>
  );
}